import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button as UIButton } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AppStoreBadges } from "@/components/AppStoreBadges";
import { SEO, generateSchemaData } from "@/components/SEO";
import { SimplePageTitle } from "@/components/PageTitle";
import { EnhancedFooter } from "@/components/EnhancedFooter";
import Header from "@/components/Header";
import {
  MapPin,
  Car,
  Clock,
  Star,
  Shield,
  CreditCard,
  Navigation,
  Smartphone,
  Users,
  CheckCircle,
  Menu,
  X,
  Zap,
  Award,
  HeadphonesIcon,
  Wifi,
  Battery,
  Coffee,
  Briefcase,
  Crown,
  TrendingUp,
  Globe,
  Phone,
  ArrowRight,
} from "lucide-react";
import {
  InternalLink,
  LocationLink,
  AirportLink,
  RouteLink,
  ServiceLink,
  ContextualLinks,
} from "@/components/InternalLink";

export default function RideEnhanced() {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Determine canonical URL based on current path
  const canonicalUrl =
    location.pathname === "/ride"
      ? "https://tigola.com/ride"
      : "https://tigola.com/";

  // Determine page title based on path
  const pageTitle =
    location.pathname === "/ride"
      ? "Tigola - Premium UK Transport & Airport Transfers"
      : "Professional Taxi Service UK | Tigola Local & Airport Taxis";

  const pageDescription =
    location.pathname === "/ride"
      ? "UK transport with fixed pricing. Professional drivers, tracking, airport transfers & business travel."
      : "Reliable taxi across Leicester, Birmingham & all UK cities. Fixed pricing, professional drivers, 24/7.";

  // Intersection Observer for animations
  useEffect(() => {
    setIsVisible(true);

    // Track page view
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "page_view", {
        page_title: "Ride Booking Page",
        page_location: window.location.href,
      });
    }
  }, []);

  // Process logo to make white pixels transparent
  useEffect(() => {
    const processLogoCanvas = (
      canvasId: string,
      desiredWidth: number,
      desiredHeight: number,
    ) => {
      const img = new Image();
      img.src =
        "https://cdn.builder.io/api/v1/image/assets%2Feeb669de10ca40b6919a1d6c2b388e0b%2F872047113d40497fb26127e740e4f65e?format=webp&width=800";
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        if (!canvas) return;

        canvas.width = desiredWidth;
        canvas.height = desiredHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, desiredWidth, desiredHeight);

        // Make white pixels transparent
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < imgData.data.length; i += 4) {
          const r = imgData.data[i];
          const g = imgData.data[i + 1];
          const b = imgData.data[i + 2];

          // If pixel is almost white, make it transparent
          if (r > 240 && g > 240 && b > 240) {
            imgData.data[i + 3] = 0;
          }
        }
        ctx.putImageData(imgData, 0, 0);
      };
    };

    // Process mobile logo (120x120)
    processLogoCanvas("logoCanvasMobile", 120, 120);
    // Process desktop logo (256x256)
    processLogoCanvas("logoCanvasDesktop", 256, 256);
  }, []);

  const features = [
    {
      icon: Navigation,
      title: "Real-time GPS Tracking",
      description:
        "Track your driver's location and get accurate arrival times with our advanced GPS system",
      color: "text-blue-600",
      link: "/help#tracking",
      linkText: "Learn about our tracking features",
    },
    {
      icon: Clock,
      title: "24/7 UK-wide Availability",
      description:
        "Request rides anytime, anywhere across the UK including all major cities and airports",
      color: "text-green-600",
      link: "/coverage",
      linkText: "View our coverage areas",
    },
    {
      icon: Shield,
      title: "Professional Licensed Drivers",
      description:
        "All drivers are DBS checked, professionally trained, and fully licensed for your safety and peace of mind",
      color: "text-purple-600",
      link: "/drive",
      linkText: "Become a Tigola driver",
    },
    {
      icon: CreditCard,
      title: "Secure Cashless Payments",
      description:
        "Pay seamlessly through the app with your preferred payment method - cards, Apple Pay, Google Pay",
      color: "text-orange-600",
      link: "/help#payments",
      linkText: "Payment options",
    },
    {
      icon: Star,
      title: "Safety & Security First",
      description:
        "Share your trip details, emergency assistance button, driver ratings, and 24/7 support for complete peace of mind",
      color: "text-red-600",
      link: "/help#safety",
      linkText: "Safety features",
    },
    {
      icon: CheckCircle,
      title: "Transparent Fixed Pricing",
      description:
        "No surge pricing, no hidden fees - see your exact fare upfront before you book. What you see is what you pay.",
      color: "text-teal-600",
      link: "/help#pricing",
      linkText: "How pricing works",
    },
  ];

  const premiumFeatures = [
    {
      icon: Wifi,
      title: "Free WiFi",
      description: "Stay connected throughout your journey",
    },
    {
      icon: Battery,
      title: "Device Charging",
      description: "USB and wireless charging available",
    },
    {
      icon: Coffee,
      title: "Refreshments",
      description: "Complimentary water and mints",
    },
    {
      icon: HeadphonesIcon,
      title: "Quiet Environment",
      description: "Professional, distraction-free travel",
    },
  ];

  const pricingExamples = [
    {
      route: "Birmingham City Centre Transport",
      routeLink: "/taxi/birmingham",
      standard: "£8-15",
      premium: "£15-25",
      business: "£20-30",
      tigolaX: "£8-15",
      tigolaXL: "£15-25",
      tigolaXXL: "£20-30",
      popular: true,
      description: "Local Birmingham transport",
    },
    {
      route: "Leicester City Centre Transport",
      routeLink: "/taxi/leicester",
      standard: "£7-12",
      premium: "£12-20",
      business: "£18-25",
      tigolaX: "£7-12",
      tigolaXL: "£12-20",
      tigolaXXL: "£18-25",
      popular: true,
      description: "Local Leicester transport",
    },
    {
      route: "Birmingham to East Midlands Airport",
      routeLink: "/airports/EMA",
      standard: "£25-35",
      premium: "£40-55",
      business: "£50-65",
      tigolaX: "£25-35",
      tigolaXL: "£40-55",
      tigolaXXL: "£50-65",
      popular: true,
      description: "Airport transfer from Birmingham",
    },
    {
      route: "Leicester to East Midlands Airport",
      routeLink: "/airports/EMA",
      standard: "£20-28",
      premium: "£35-48",
      business: "£45-58",
      tigolaX: "£20-28",
      tigolaXL: "£35-48",
      tigolaXXL: "£45-58",
      popular: false,
      description: "Airport transfer from Leicester",
    },
    {
      route: "Birmingham to Leicester Intercity",
      routeLink: "/taxi/leicester",
      standard: "£35-45",
      premium: "£55-70",
      business: "£65-80",
      tigolaX: "£35-45",
      tigolaXL: "£55-70",
      tigolaXXL: "£65-80",
      popular: false,
      description: "Intercity travel between Birmingham and Leicester",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Business Executive",
      content:
        "Tigola has transformed my airport transfers. Always punctual, professional drivers, and the app makes booking so easy.",
      rating: 5,
      location: "London",
    },
    {
      name: "Michael Chen",
      role: "Frequent Traveler",
      content:
        "The fixed pricing is brilliant - no surprises. Premium service feels worth every penny.",
      rating: 5,
      location: "Manchester",
    },
    {
      name: "Emma Williams",
      role: "Corporate Client",
      content:
        "Our company relies on Tigola for executive transport. Consistent quality and excellent customer service.",
      rating: 5,
      location: "Birmingham",
    },
  ];

  const stats = [
    { number: "500K+", label: "Happy Customers", icon: Users },
    { number: "50+", label: "UK Cities", icon: Globe },
    { number: "4.9", label: "Average Rating", icon: Star },
    { number: "24/7", label: "Customer Support", icon: Phone },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="UK transport service, airport transfers, professional drivers UK, luxury car service, private hire UK, taxi alternative, fixed pricing transport, executive car hire, chauffeur service UK, intercity travel UK, premium rides, ride booking app, Birmingham airport transfer, Manchester transport, London rides, professional transport"
        canonical={canonicalUrl}
        ogUrl={canonicalUrl}
        ogImage="https://tigola.com/tigola-logo.png"
        schemaData={[
          // Homepage Website Schema
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Tigola",
            alternateName: "Tigola UK Transport Service",
            url: "https://tigola.com",
            description:
              "Premium UK transport and airport transfer service with professional drivers",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://tigola.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
            inLanguage: "en-GB",
            publisher: {
              "@type": "Organization",
              name: "Tigola UK Ltd",
              logo: "https://tigola.com/tigola-logo.png",
            },
          },
          // Enhanced Organization Schema for Homepage using standardized schema
          generateSchemaData.organization(),
          // Service Schema
          generateSchemaData.service(
            "Professional Transport Service",
            "Premium transport service across the UK with professional drivers and luxury vehicles",
          ),
          // Product Schema
          {
            "@type": "Product",
            name: "Tigola Transport Service",
            description: "Professional transport booking service across the UK",
            brand: {
              "@type": "Brand",
              name: "Tigola",
            },
            offers: {
              "@type": "Offer",
              priceCurrency: "GBP",
              price: "8.00",
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "GBP",
                price: "8.00",
              },
              availability: "https://schema.org/InStock",
              seller: {
                "@type": "Organization",
                name: "Tigola UK Ltd",
              },
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: 4.9,
              reviewCount: 500000,
            },
          },
        ]}
      />

      {/* Header */}
      <Header />

      {/* Hero Section with Enhanced Booking */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Homepage indicator - remove breadcrumb since this IS the homepage */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div
              className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div className="mb-8">
                <div className="flex items-center gap-6 md:gap-12 mb-4">
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight text-gray-900">
                    Ride with
                    <span className="text-primary block">Tigola</span>
                  </h1>
                  {/* Logo on mobile/tablet - next to heading */}
                  <div className="lg:hidden flex-shrink-0">
                    <div
                      style={{
                        display: "inline-block",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      <canvas
                        id="logoCanvasMobile"
                        style={{
                          display: "block",
                          width: "120px",
                          height: "120px",
                        }}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xl md:text-2xl leading-relaxed text-gray-600">
                  Affordable rides across the UK with Tigola. A budget-friendly
                  alternative to Uber and Bolt. Professional drivers, fixed
                  pricing, and real-time tracking. Prices vary by location with
                  24/7 availability.
                </p>
              </div>

              {/* Key Benefits */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">Fixed pricing</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">
                    Real-time tracking
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">24/7 availability</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">
                    Professional drivers
                  </span>
                </div>
              </div>

              {/* App Store Badges */}
              <div className="mb-2">
                <h3 className="text-lg font-semibold mb-4">
                  Download the Tigola App
                </h3>
                <AppStoreBadges direction="row" size="md" />
              </div>
            </div>

            {/* Tigola App Logo - Desktop Only (right side) */}
            <div
              className={`hidden lg:flex items-center justify-center transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div
                style={{
                  display: "inline-block",
                  borderRadius: "16px",
                  overflow: "hidden",
                }}
              >
                <canvas
                  id="logoCanvasDesktop"
                  style={{
                    display: "block",
                    width: "256px",
                    height: "256px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 text-primary mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why choose Tigola for your UK transport needs?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference with professional transport designed
              around your needs. From{" "}
              <InternalLink
                href="/airports"
                className="text-primary hover:underline font-medium"
              >
                airport transfers
              </InternalLink>{" "}
              to{" "}
              <InternalLink
                href="/business"
                className="text-primary hover:underline font-medium"
              >
                executive business travel
              </InternalLink>
              , we've got you covered across the UK.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="text-center hover:shadow-lg transition-shadow duration-300"
              >
                <CardContent className="pt-6">
                  <feature.icon
                    className={`h-12 w-12 mx-auto mb-4 ${feature.color}`}
                  />
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{feature.description}</p>
                  {feature.link && (
                    <InternalLink
                      href={feature.link}
                      className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1"
                    >
                      {feature.linkText}
                      <ArrowRight className="h-3 w-3" />
                    </InternalLink>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Tigola Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
            Professional UK Transport at Competitive Rates
          </h2>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
            <p>
              Tigola is the UK's premium transport service, providing
              professional rides across every major city and airport. Whether
              you need a reliable airport transfer, a business ride, or everyday
              local transport, Tigola delivers consistent quality with
              transparent, fixed pricing—no surge pricing, no hidden fees.
            </p>
            <p>
              With over 500,000 satisfied customers and licensed, thoroughly
              vetted drivers, we've revolutionized the way people travel in the
              UK. Our fleet includes luxury saloons, spacious estates, and MPVs
              for groups, ensuring the right vehicle for your journey. Real-time
              GPS tracking lets you monitor your ride from booking to arrival,
              while our 24/7 customer support team is always ready to help.
            </p>
            <p>
              From Leicester and Birmingham to London and beyond, Tigola covers
              100+ UK cities and towns. We specialize in airport transfers to
              Heathrow, Gatwick, Manchester, Birmingham, Edinburgh, and 15+
              other major airports with flight tracking and meet-and-greet
              services. Book in advance for guaranteed availability, or request
              an immediate ride through our app or website.
            </p>
            <h3 className="text-2xl font-semibold text-gray-900 mt-8">
              The Tigola Difference
            </h3>
            <ul className="space-y-3">
              <li>
                ✓ <strong>Fixed Pricing:</strong> Know your fare upfront with
                transparent, competitive rates from £8.
              </li>
              <li>
                ✓ <strong>Professional Drivers:</strong> All drivers are
                licensed, insured, and background-checked for your safety.
              </li>
              <li>
                ✓ <strong>Real-Time Tracking:</strong> Monitor your ride live
                with GPS tracking and driver details.
              </li>
              <li>
                ✓ <strong>Fleet Variety:</strong> Choose from saloons, estates,
                and MPVs to suit your needs.
              </li>
              <li>
                ✓ <strong>24/7 Availability:</strong> Book anytime—early
                mornings, late nights, weekends, holidays.
              </li>
              <li>
                ✓ <strong>Advanced Booking:</strong> Reserve your ride up to 6
                months ahead for flights, events, and meetings.
              </li>
              <li>
                ✓ <strong>Business Accounts:</strong> Monthly billing, corporate
                rates, and dedicated support for companies.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Premium Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-purple-600 to-pink-600">
              Premium & Business
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Enhanced comfort features
            </h2>
            <p className="text-xl text-gray-600">
              Elevate your journey with premium amenities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {premiumFeatures.map((feature, index) => (
              <div key={index} className="text-center p-6">
                <feature.icon className="h-10 w-10 text-primary mx-auto mb-3" />
                <h4 className="font-semibold mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Examples */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Transparent UK transport pricing
            </h2>
            <p className="text-xl text-gray-600">
              See what you'll pay before you book - no surge pricing, ever. Book{" "}
              <InternalLink
                href="/airports"
                className="text-primary hover:underline font-medium"
              >
                airport transfers
              </InternalLink>
              ,{" "}
              <InternalLink
                href="/taxi"
                className="text-primary hover:underline font-medium"
              >
                city transport
              </InternalLink>
              , or{" "}
              <InternalLink
                href="/business"
                className="text-primary hover:underline font-medium"
              >
                executive business travel
              </InternalLink>{" "}
              with confidence.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg shadow-sm overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Popular Routes
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <Car className="h-4 w-4" />
                      Tigola X
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <Crown className="h-4 w-4" />
                      Tigola XL
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Tigola XXL
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pricingExamples.map((example, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {example.routeLink ? (
                            <InternalLink
                              href={example.routeLink}
                              className="text-primary hover:underline font-semibold"
                            >
                              {example.route}
                            </InternalLink>
                          ) : (
                            <span>{example.route}</span>
                          )}
                          {example.popular && (
                            <Badge variant="secondary" className="text-xs">
                              Popular
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {example.description}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-primary font-semibold">
                      {example.tigolaX}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-primary font-semibold">
                      {example.tigolaXL}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-primary font-semibold">
                      {example.tigolaXXL}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Price Disclaimer:</span> The
              prices shown above are approximate estimates and may vary based on
              real-time demand, route distance, and vehicle availability. For
              the most up-to-date and accurate pricing, please use our{" "}
              <InternalLink
                href="/book-online"
                className="text-primary hover:underline font-medium"
              >
                Book Online
              </InternalLink>{" "}
              option or download the{" "}
              <InternalLink
                href="/app"
                className="text-primary hover:underline font-medium"
              >
                Tigola Rider App
              </InternalLink>{" "}
              to get an instant quote for your specific journey.
            </p>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              Get instant quotes for any UK destination
            </p>
            <UIButton
              size="lg"
              onClick={() => (window.location.href = "/book-online")}
            >
              Calculate Your Fare
            </UIButton>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What our customers say
            </h2>
            <p className="text-xl text-gray-600">
              Join hundreds of thousands of satisfied customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="hover:shadow-lg transition-shadow duration-300"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-sm text-gray-500">
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contextual Internal Links */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContextualLinks pageType="homepage" maxLinks={9} />
        </div>
      </section>

      {/* App Download CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-blue-600 text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2Feeb669de10ca40b6919a1d6c2b388e0b%2F9fd830a9f53142d9858e45b388e09bd9?format=webp&width=800"
            alt="Tigola Rider App Logo"
            className="h-16 w-16 mx-auto mb-6 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-black/10"
            loading="lazy"
          />
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Get the Tigola app
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Book rides instantly, track your driver in real-time, and manage all
            your trips in one place.
          </p>
          <div className="flex justify-center">
            <AppStoreBadges direction="row" size="lg" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <EnhancedFooter />
    </div>
  );
}
